import { Share2, Copy, Twitter, Facebook, Mail } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from '@/components/ui/button';

interface ShareProfileButtonProps {
    username: string;
}

const ShareProfileButton = ({ username }: ShareProfileButtonProps) => {
    const userlink = `allmygigs.com/${username}`

    const createTwitterShareUrl = (): string => {
        const text = `Check out ${username} on allmygigs`;
        return `https://twitter.com/intent/tweet?url=${encodeURIComponent(userlink)}&text=${encodeURIComponent(text)}`;
    };

    const createFacebookShareUrl = (): string => {
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(userlink)}`;
    };

    const createEmailShareUrl = (): string => {
        const subject = `Check out ${username} on allmygigs`;
        const body = `Hi,\n\nI thought you might be interested in checking out ${username}'s upcoming gigs on allmygigs.com.\n\nLink: ${userlink}\n\nRegards,`;
        return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body.replace(/\n/g, '\r\n'))}`;
    };

    const copyLinkToClipboard = async () => {
        if (!userlink) {
            alert('No event URL available to copy.');
            return;
        }
        try {
            await navigator.clipboard.writeText(userlink);
            alert('Link copied, ready to paste!'); // Consider using a more subtle toast notification
        } catch (err) {
            console.error('Failed to copy link: ', err);
            alert('Failed to copy link.');
        }
    };

    return (
        <AlertDialog>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <AlertDialogTrigger asChild>
                            <div className="group relative">
                                <div className="absolute inset-0 rounded-full bg-sky-300 opacity-0 animate-pulse-glow group-hover:opacity-20 transition-opacity duration-300"></div>

                                <Button
                                    variant="ghost"
                                    className="relative rounded-full bg-violet-100 hover:bg-violet-200 transition-colors
                                                                              px-3 py-1.5 h-auto text-sm text-violet-600
                                                                              font-medium ring-2 ring-violet-200 ring-offset-1 ring-offset-white
                                                                              hover:ring-violet-300 focus:ring-violet-400"
                                >
                                    <Share2 className="h-4 w-4 text-violet-800 mr-1.5 inline-block" />
                                    <span className="absolute inset-0 rounded-full bg-gradient-to-r from-sky-200 to-transparent
                                                    opacity-0 group-hover:opacity-30 blur-sm transition-opacity duration-300"></span>
                                </Button>
                            </div>
                        </AlertDialogTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Share {username}'s profile</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <AlertDialogContent className="w-full max-w-sm">
                <AlertDialogHeader>
                    <AlertDialogTitle>Share {username}'s profile</AlertDialogTitle>
                    <AlertDialogDescription>
                        Choose how you'd like to share {username}'s profile.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
                    {userlink && (
                        <AlertDialogAction
                            className="w-full flex items-center justify-center"
                            onClick={copyLinkToClipboard}
                        >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Link
                        </AlertDialogAction>
                    )}
                    <AlertDialogAction
                        className="w-full flex items-center justify-center"
                        onClick={() => window.open(createTwitterShareUrl(), '_blank', 'noopener,noreferrer')}
                    >
                        <Twitter className="h-4 w-4 mr-2" />
                        Share on Twitter
                    </AlertDialogAction>
                    <AlertDialogAction
                        className="w-full flex items-center justify-center"
                        onClick={() => window.open(createFacebookShareUrl(), '_blank', 'noopener,noreferrer')}
                    >
                        <Facebook className="h-4 w-4 mr-2" />
                        Share on Facebook
                    </AlertDialogAction>
                    <AlertDialogAction
                        className="w-full flex items-center justify-center"
                        onClick={() => window.location.href = createEmailShareUrl()}
                    >
                        <Mail className="h-4 w-4 mr-2" />
                        Share via Email
                    </AlertDialogAction>
                    <AlertDialogCancel className="w-full mt-2">Cancel</AlertDialogCancel>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default ShareProfileButton;